import { CalendarIcon } from "@chakra-ui/icons";
import { Box, InputGroup, InputRightElement } from "@chakra-ui/react";
import { Instant, LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { TableCellBadge } from "../../../../communication/components/TableCellBadge";
import { Messages, ResponseOf } from "../../../../../core/api";
import RangeDatePicker from "../../../../../shared/components/DatePicker/RangeDatePicker";
import Select from "../../../../../shared/components/Select";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileFilterBar, {
  EntityProfileFilterBarItem,
} from "../../../../entityProfileModal/components/EntityProfileFilterBar";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import useApi from "../../../../../shared/hooks/useApi";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";

type Props = {
  caregiverId: CaregiverId;
};

const defaultFaxStatuses: Messages["FaxStatus"][] = [
  "processing",
  "failed",
  "success",
  "transmitting",
];

const faxStatusOptions: { label: string; value: Messages["FaxStatus"] }[] = [
  { label: "PROCESSING", value: "processing" },
  { label: "FAILED", value: "failed" },
  { label: "SUCCESS", value: "success" },
  { label: "TRANSMITTING", value: "transmitting" },
];

const faxStatusLabels: Record<Messages["FaxStatus"], string> = {
  failed: "Failed",
  processing: "Processing",
  success: "Success",
  transmitting: "Transmitting",
};

const faxStatusTextToColor = {
  failed: "red" as const,
  processing: "yellow" as const,
  success: "green" as const,
  transmitting: "yellow" as const,
};

const faxPageSizes = [15, 25, 50, 100] as const;

type FaxRow = ResponseOf<"get", "./faxes">["faxes"][number];
type FaxSortKey = "createdAt";

function toEpochMillis(value: FaxRow["createdAt"]) {
  return value instanceof Instant ? value.toEpochMilli() : new Date(String(value)).getTime();
}

export default function CaregiverProfileFaxesSection(props: Props) {
  const { api } = useApi();
  const [selectedStatuses, setSelectedStatuses] =
    React.useState<Messages["FaxStatus"][]>(defaultFaxStatuses);
  const [createdAtFrom, setCreatedAtFrom] = React.useState<LocalDate | null>(() =>
    LocalDate.now().minusDays(29)
  );
  const [createdAtTo, setCreatedAtTo] = React.useState<LocalDate | null>(() => LocalDate.now());
  const faxesQuery = useQuery({
    queryKey: [
      "caregiverProfileModal",
      props.caregiverId,
      "faxes",
      selectedStatuses,
      createdAtFrom,
      createdAtTo,
    ],
    queryFn: () =>
      api.get("./faxes", {
        query: {
          caregiver: props.caregiverId,
          createdAtFrom: createdAtFrom ?? undefined,
          createdAtTo: createdAtTo ?? undefined,
          faxStatus: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        },
      }),
  });
  const handleStatusChange = (statuses: Messages["FaxStatus"][] | undefined) => {
    setSelectedStatuses(statuses ?? []);
  };
  const handleCreatedAtChange = (value: [LocalDate, LocalDate | null] | null) => {
    setCreatedAtFrom(value?.[0] ?? null);
    setCreatedAtTo(value?.[1] ?? null);
  };
  const getStatusSelectLabel = (statuses: Messages["FaxStatus"][]) => {
    if (statuses.length === defaultFaxStatuses.length) {
      return "All statuses";
    }

    if (statuses.length === 0) {
      return "None";
    }

    return statuses.map((status) => faxStatusLabels[status]).join(", ");
  };
  const filters = (
    <EntityProfileFilterBar>
      <EntityProfileFilterBarItem label="Created At" maxW={{ base: "100%", md: "300px" }}>
        <InputGroup maxW="300px">
          <RangeDatePicker
            dateFormat="MM/dd/yyyy"
            endDate={createdAtTo}
            inputProps={{ h: "34px", fontSize: "14px", width: "full" }}
            selected={createdAtTo}
            startDate={createdAtFrom}
            onChange={handleCreatedAtChange}
          />
          <InputRightElement h="34px">
            <CalendarIcon color={entityProfileModalColors.muted} />
          </InputRightElement>
        </InputGroup>
      </EntityProfileFilterBarItem>
      <EntityProfileFilterBarItem label="Status" maxW={{ base: "100%", md: "220px" }}>
        <Select
          multiple
          buttonProps={{
            bg: entityProfileModalColors.surface,
            borderColor: entityProfileModalColors.inputBorder,
            borderRadius: "4px",
            color: entityProfileModalColors.title,
            fontSize: "14px",
            fontWeight: 400,
            h: "34px",
            maxW: "220px",
            minW: "220px",
          }}
          label="Select"
          options={faxStatusOptions}
          searchable={false}
          selectedLabel={getStatusSelectLabel}
          value={selectedStatuses}
          width="220px"
          onChange={handleStatusChange}
        />
      </EntityProfileFilterBarItem>
    </EntityProfileFilterBar>
  );
  const faxes = faxesQuery.data?.faxes ?? [];
  const leftHeaderProps = React.useMemo(() => ({ textAlign: "left" as const }), []);
  const columns = React.useMemo<Array<EntityProfileDataTableColumn<FaxRow, FaxSortKey>>>(
    () => [
      {
        headerProps: leftHeaderProps,
        key: "type",
        header: "Type",
        render: (fax) => fax.type,
      },
      {
        headerProps: leftHeaderProps,
        key: "createdAt",
        header: "Created At",
        render: (fax) => dateFormatter.toDateOrDateTime(fax.createdAt),
        sortKey: "createdAt",
        sortValue: (fax) => toEpochMillis(fax.createdAt),
      },
      {
        headerProps: leftHeaderProps,
        key: "status",
        header: "Status",
        render: (fax) => (
          <TableCellBadge
            color={faxStatusTextToColor[fax.status]}
            text={fax.status.toUpperCase()}
          />
        ),
      },
      {
        headerProps: leftHeaderProps,
        key: "createdBy",
        header: "Created By",
        render: (fax) => fax.createdByUserName ?? "",
      },
      {
        headerProps: leftHeaderProps,
        key: "sentFrom",
        header: "Sent From",
        render: (fax) => phoneFormatter.formatNationalIfValid(fax.sender),
      },
      {
        headerProps: leftHeaderProps,
        key: "sentTo",
        header: "Sent To",
        render: (fax) => phoneFormatter.formatNationalIfValid(fax.recipient),
      },
    ],
    [leftHeaderProps]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load caregiver faxes."
      filters={filters}
      isError={faxesQuery.isError}
      isLoading={faxesQuery.isLoading}
      loadingLabel="Loading caregiver faxes"
      onRetry={() => void faxesQuery.refetch()}
    >
      <Box minH="220px">
        <EntityProfileDataTable
          columns={columns}
          defaultPageSize={faxPageSizes[0]}
          defaultSort={{ key: "createdAt", direction: "asc" }}
          getRowKey={(fax) => fax.id}
          pageSizeOptions={[...faxPageSizes]}
          rows={faxes}
          showEmptyState={false}
        />
      </Box>
    </EntityProfileQuerySection>
  );
}
